import { Injectable, inject } from '@angular/core';
import {
    HttpEvent,
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpResponse,
} from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from '../auth-helpers/auth.service';
import { FormErrorsService } from '../services/form-errors.service';
import { HttpRequestType } from '../models/requests/request';
import { ToastService } from '../services/toast.service';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
    private translateService = inject(TranslateService);
    private _toastService = inject(ToastService);
    private authService = inject(AuthService);
    private formErrorsService = inject(FormErrorsService);

    private readonly TTL = 60 * 60 * 0.5; // 30 min
    private baseUrl: string;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        this.baseUrl = environment.urls.api;
    }

    public intercept(
        request: HttpRequest<HttpRequestType>,
        next: HttpHandler,
    ): Observable<HttpEvent<unknown>> {
        // Skipping the translation files
        if (request.url.match(/.*\.json/)) {
            return next.handle(request);
        }
        return this.doRequest(request, next);
    }

    private doRequest(
        request: HttpRequest<HttpRequestType>,
        next: HttpHandler,
    ): Observable<HttpEvent<unknown>> {
        const token = localStorage.getItem('gsr-user-token');
        if (this.isGSRApi(request)) {
            const headers = {
                'X-Auth': `${token}`,
                'Accept-Language': this.translateService.currentLang,
            };
            request = request.clone({ setHeaders: headers });
        }
        return next.handle(request).pipe(
            tap({
                next: (event) => {
                    if (event instanceof HttpResponse) {
                        if (
                            request.method !== 'GET' &&
                            request.url !== `${this.baseUrl}/login/` &&
                            request.url !== `${this.baseUrl}/logout/` &&
                            request.url !== `${this.baseUrl}/recapcha/`
                        ) {
                            this._toastService.show({
                                classname: 'bg-success text-white rounded-2',
                                delay: 3000,
                                message: this.translateService.instant(
                                    'PHRASES.SUCCESS_MSG',
                                ),
                            });
                        }
                    }
                },
            }),
            catchError((err) => {
                this.handleError(err);
                return throwError(() => err);
            }),
        );
    }

    private isGSRApi(request: HttpRequest<HttpRequestType>): boolean {
        return request.url.indexOf(environment.urls.api) >= 0;
    }

    private handleError(err) {
        if ([401, 403].includes(err.status) && this.authService.userValue) {
            this.authService.logout();
        }
        const error = err.error.message || err.error.label || err.statusText;
        this.openSnackBar(error);
        this.handleFormErrors(err);
    }

    private handleFormErrors(error) {
        if (error.error.error === 'validation_errors') {
            this.formErrorsService.setHttpErrors(error.error.details);
        }
    }

    private openSnackBar(message: string) {
        this._toastService.show({
            classname: 'bg-danger text-white rounded-2',
            delay: 2000,
            message: message,
        });
    }
}
